import { Plant } from "../../table_configuration/Project"
import { PalantirTextField, PalantirDispatchedTextField, makeTextMaskNumber } from "../../../components/input/Text"
import { PalantirAutocomplete, PalantirBooleanSelector, PalantirDatePicker, PalantirSelector, } from "../../../components/input/SelectPicker"
import { useChangeLog } from "../../../hooks/changeLog"
import { useDBViewFormValidation } from "../../../hooks/databaseViewFormValidation"
import ViewPanel from "../ViewPanel"
import { generateUUID } from "../../../utils/databaseAppUtils"
import { CardHeaderWithMenu } from "../../../components/panelv2/Panel2"
import { OEM } from "../../table_configuration/Counterparty"
import React, { useEffect, useState } from "react"
import { DialogProvider, useDialog } from "../../../components/dialog/DialogProvider"
import { DialogContentText } from "@mui/material"
import { PalantirBasicCRUDMenu } from "../../../components/menuv2/Menu"
import { Text, SegmentedControl, Badge, Alert } from "@mantine/core"
import { Counterparty } from "../../table_configuration/Entity"
import _ from 'lodash'
import { booleanOptions } from "../../table_configuration/CommonColumns"
import { BESSSpec, InverterSpec, ModuleSpec, TrackerSpec } from "../../table_configuration/EquipmentSpec"
import { formatDateDisplay } from "../../../utils/Dates"
import { AddFloaterButtonWithPrompt, DeleteFloaterButtonWithPrompt } from "../../../components/button/FloaterButtonWithPrompt"
import { ConstructionActivitySchedule, ConstructionActivity, ConstructionActivityScheduleEntry } from "../../table_configuration/Reporting"


const TextMaskNumber = makeTextMaskNumber({
    scale: 4
})

const makeScheduleEntryRecord = (scheduleId, activityId) => {
    const record = ConstructionActivityScheduleEntry.buildNewRecord()
    record[ConstructionActivityScheduleEntry.columnSchema.oid] = generateUUID()
    record[ConstructionActivityScheduleEntry.columnSchema.scheduleId] = scheduleId
    record[ConstructionActivityScheduleEntry.columnSchema.activityId] = activityId
    return record
}


export default function ConstructionActivitySchedulePanelProxy(props) {

    const activitySchedules = props.data[ConstructionActivitySchedule.buildId()].filter(x => x[ConstructionActivitySchedule.columnSchema.projectId]===props.selectedPlantId)
    const activityScheduleIds = activitySchedules.map(x => x[ConstructionActivitySchedule.columnSchema.oid])
    const activityScheduleEntries = props.data[ConstructionActivityScheduleEntry.buildId()].filter(x => activityScheduleIds.includes(x[ConstructionActivityScheduleEntry.columnSchema.scheduleId]))
    // Only need activities relevant to the plant technology
    const activities = props.data[ConstructionActivity.buildId()].filter(x => x[ConstructionActivity.columnSchema.applicableTechnologyType].split(",").includes(props.selectedPlant[Plant.columnSchema.technology]))

    return (
        <DialogProvider>
        <ConstructionActivitySchedulePanel
            initialActivityScheduleRecords={activitySchedules}
            initialActivityScheduleEntryRecords={activityScheduleEntries}
            activityRecords={activities}
            {...props}
        />
        </DialogProvider>
    )
}

function ConstructionActivitySchedulePanel({initialActivityScheduleRecords, initialActivityScheduleEntryRecords, activityRecords, ...props}) {

    const [
        scheduleRecords, prepareScheduleLog, updateScheduleLog, addToScheduleLog,
        deleteFromScheduleLog, bulkOpOnScheduleLog, mergeAndResetScheduleLog, resetScheduleLog, popFromScheduleLog
    ] = useChangeLog(initialActivityScheduleRecords, ConstructionActivitySchedule)
    const [
        scheduleEntryRecords, prepareScheduleEntryLog, updateScheduleEntryLog, addToScheduleEntryLog,
        deleteFromScheduleEntryLog, bulkOpOnScheduleEntryLog, mergeAndResetScheduleEntryLog, resetScheduleEntryLog, popFromScheduleEntryLog
    ] = useChangeLog(initialActivityScheduleEntryRecords, ConstructionActivityScheduleEntry)
    const [isCreating, setIsCreating] = useState(false)
    
    const [selectedScheduleId, setSelectedScheduleId] = useState(null)
    const selectedSchedule = selectedScheduleId ? scheduleRecords.find(x => x[ConstructionActivitySchedule.columnSchema.oid]===selectedScheduleId) : null

    const filteredScheduleEntries = scheduleEntryRecords.filter(x => x[ConstructionActivityScheduleEntry.columnSchema.scheduleId]===selectedScheduleId)

    const [errors, addErrors, removeErrors, setErrors, verifyChangelogSubmission, resetErrors] = useDBViewFormValidation()
    const [openDialog, closeDialog] = useDialog();


    const makeScheduleName = (date) => {
        const displayDate = (!isNaN(date) && date) ? formatDateDisplay(date) : null
        const name = displayDate ? `${props.selectedPlant[Plant.columnSchema.plantName]} - ${displayDate}` : null
        return name
    }

    const updateScheduleLogProxy = (update) => updateScheduleLog(selectedSchedule, update)
    /* Functions to create a new block */
    const addToScheduleLogProxy = () => {
        const newSchedule = ConstructionActivitySchedule.buildNewRecord()
        const scheduleId = generateUUID()
        newSchedule[ConstructionActivitySchedule.columnSchema.oid] = scheduleId
        newSchedule[ConstructionActivitySchedule.columnSchema.projectId] = props.selectedPlantId
        // If there are existing schedules, copy the record with the latest date field
        if (scheduleRecords.length > 0) {
            const latestSchedule = _.maxBy(scheduleRecords, (x) => x[ConstructionActivitySchedule.columnSchema.date])
            const latestScheduleId = latestSchedule[ConstructionActivitySchedule.columnSchema.oid]
            addToScheduleEntryLogProxy(scheduleId, latestScheduleId)
        }
        else addToScheduleEntryLogProxy(scheduleId, null)
        setSelectedScheduleId(scheduleId)
        bulkOpOnScheduleLog([{
            method: "clear"
        }, {
            method: "insert", changes: [newSchedule]
        }])
    }
    const addToScheduleEntryLogProxy = (scheduleId, scheduleToCopyId) => {
        // Since the schedule entry records changelog has not yet been flushed, any uncommitted changes will be copied to the new records.
        // This isn't ideal behavior, but the likelihood if this happening is low and so are the consequences.
        // And to address this is not worht the effort now.
        const existingScheduleEntryRecords = scheduleEntryRecords.filter(x => x[ConstructionActivityScheduleEntry.columnSchema.scheduleId]===scheduleId)
        const existingActivityIds = existingScheduleEntryRecords.map(x => x[ConstructionActivityScheduleEntry.columnSchema.activityId])

        let newScheduleEntryRecords = []
        if (scheduleToCopyId) {
            const scheduleEntryRecordsToCopy = scheduleEntryRecords.filter(x => {
                return (
                    x[ConstructionActivityScheduleEntry.columnSchema.scheduleId]===scheduleToCopyId &&
                    activityRecords.map(y => y[ConstructionActivity.columnSchema.oid]).includes(x[ConstructionActivityScheduleEntry.columnSchema.activityId]) &&
                    !existingActivityIds.includes(x[ConstructionActivityScheduleEntry.columnSchema.activityId])
                )
            })
            newScheduleEntryRecords = scheduleEntryRecordsToCopy.map(x => {
                const newRecord = Object.assign({}, x)
                newRecord[ConstructionActivityScheduleEntry.columnSchema.oid] = generateUUID()
                newRecord[ConstructionActivityScheduleEntry.columnSchema.scheduleId] = scheduleId
                return newRecord
            })
        }

        newScheduleEntryRecords.push(...activityRecords.filter(x => {
            return (
                !existingActivityIds.includes(x[ConstructionActivity.columnSchema.oid]) &&
                newScheduleEntryRecords.map(y => y[ConstructionActivityScheduleEntry.columnSchema.activityId]).includes(x[ConstructionActivity.columnSchema.oid])===false
            )
        }).map(x => makeScheduleEntryRecord(scheduleId, x[ConstructionActivity.columnSchema.oid])))
        console.log(newScheduleEntryRecords)

        const deleteRecords = existingScheduleEntryRecords.filter(x => activityRecords.map(x => x[ConstructionActivity.columnSchema.oid]).includes(x[ConstructionActivityScheduleEntry.columnSchema.activityId])===false)
        console.log(deleteRecords)

        if (newScheduleEntryRecords.length === 0 && deleteRecords.length === 0) return

        bulkOpOnScheduleEntryLog([{
            method: "clear"
        }, {
            method: "delete", changes: deleteRecords
        }, {
            method: "insert", changes: newScheduleEntryRecords
        }])
    }
    

    const createSchedule = () => {
        const create = () => {
            addToScheduleLogProxy()
            resetErrors()
            setIsCreating(true)
        }
        if (isCreating) {
            window.alert("You may only add one at a time.")
            return
        }
        if (prepareScheduleLog().length > 0 || prepareScheduleEntryLog().length > 0) {
            openDialog({
                title: "You have unsaved edits.",
                body: <DialogContentText>Are you sure you want to discard your changes?</DialogContentText>,
                onAccept: () => {
                    closeDialog()
                    create()
                }
            });
        }
        else create()
    }

    const selectSchedule = (record) => {
        const scheduleId = record[ConstructionActivitySchedule.columnSchema.oid]
        if (scheduleId===selectedScheduleId) return
        tryPurgeChanges(prepareScheduleLog(), prepareScheduleEntryLog(), () => setSelectedScheduleId(scheduleId))
    }
    const tryPurgeChanges = (log1, log2, callback) => {
        const purge = () => {
            if (callback) callback()
            resetScheduleLog()
            resetScheduleEntryLog()
            resetErrors()
            setIsCreating(false)
        }
        if (log1.length > 0 || log2.length > 0) {
            openDialog({
                title: "You have unsaved edits.",
                body: <DialogContentText>Are you sure you want to discard your changes?</DialogContentText>,
                onAccept: () => {
                    closeDialog()
                    purge()
                }
            });
        }
        else {
            purge()
        }
    }

    const deleteSchedule = (callback) => {
        openDialog({
            title: "Watch out!",
            body: <DialogContentText>Are you sure you want to delete the selected Schedule and associated entries?</DialogContentText>,
            onAccept: () => {
                closeDialog()
                setSelectedScheduleId(null)
                deleteFromScheduleLog(selectedSchedule)
                deleteFromScheduleEntryLog(filteredScheduleEntries)
                setTimeout(() => {
                    callback({
                        onFail: () => {
                            resetErrors()
                            setSelectedScheduleId(selectedScheduleId)
                            popFromScheduleLog()
                            popFromScheduleEntryLog(0)
                        }
                    })
                }, 100)
            }
        });
    }

    return (
        <div className="flow-horizontal fill-parent" style={{overflow: "auto"}}>
            <PalantirBasicCRUDMenu
                title="Activity Schedules"
                selectedId={selectedScheduleId}
                records={_.sortBy(scheduleRecords, (x) => x[ConstructionActivitySchedule.columnSchema.date])}
                recordIdCol={ConstructionActivitySchedule.columnSchema.oid}
                recordNameCol={ConstructionActivitySchedule.columnSchema.name}
                onSelect={selectSchedule}
                onAdd={createSchedule}
                style={{marginRight: "20px", marginTop: "25px", width: "225px", flexShrink: 0}}
            />
            <ViewPanel
                services={props.services}
                submitUrl='/api/precious/table/construction_activity_schedule'
                verifySubmit={(payload) => {
                    return verifyChangelogSubmission(
                        {
                            changeLog: payload.scheduleChangeLog,
                            checks: [{
                                checkColumn: ConstructionActivitySchedule.columnSchema.date,
                                checkFunction: "nullCheck",
                                errMessage: "Please enter a date.",
                                path: [
                                    {name: ConstructionActivitySchedule.buildId(), type: "static"},
                                    {name: ConstructionActivitySchedule.columnSchema.oid, type: "eval"}
                                ]
                            }]
                        }, {
                            changeLog: payload.scheduleEntryChangeLog,
                            checks: []
                        }
                    )
                }}
                onSubmitSuccess={(response, requestPayload) => {
                    props.handleUpdate(false)
                    resetErrors()
                    mergeAndResetScheduleLog()
                    mergeAndResetScheduleEntryLog()
                    setIsCreating(false)
                }}
                showDelete={!isCreating && selectedScheduleId}
                onDelete={deleteSchedule}
                onSubmitError={null}
                buildSubmitPayload={() => {
                    return {
                        scheduleChangeLog: prepareScheduleLog(),
                        scheduleEntryChangeLog: prepareScheduleEntryLog(),
                    }
                }}
                style={{minWidth: "440px"}}
            >
                {isCreating && <Alert style={{marginBottom: "10px"}}>The fields in this schedule were inherited from the most recent existing schedule.</Alert>}
                {selectedSchedule ?
                    <ActivitySchedule
                        key={selectedScheduleId}
                        schedule={selectedSchedule}
                        makeScheduleName={makeScheduleName}

                        activityRecords={activityRecords}
                        entryRecords={filteredScheduleEntries}
                        
                        updateSchedule={updateScheduleLogProxy}
                        updateEntry={updateScheduleEntryLog}
                        addEntries={addToScheduleEntryLogProxy}

                        errors={errors}
                        removeErrors={removeErrors}
                    /> :
                    <div className="flow-vertical" style={{textAlign: "center"}}>Please select an Activity Schedule.</div>
                }
            </ViewPanel>
        </div>
    )
}

const ActivitySchedule = ({schedule, updateSchedule, updateEntry, entryRecords, activityRecords, addEntries, makeScheduleName, errors, removeErrors}) => {

    const scheduleErrorPath = [ConstructionActivitySchedule.buildId(), schedule[ConstructionActivitySchedule.columnSchema.oid]]
    const scheduleError = errors.get(...scheduleErrorPath)
    const scheduleErrorProps = scheduleError ? {error: true, helperText: scheduleError.getMessage()} : {}

    useEffect(() => {
        addEntries(schedule[ConstructionActivitySchedule.columnSchema.oid], null)
    }, [schedule[ConstructionActivitySchedule.columnSchema.oid]])

    return (
        <div className="form-instance2">
            <CardHeaderWithMenu
                label="Activity Schedule"
            />
            <div className="body flow-vertical" style={{paddingTop: "10px"}}>
                <div className="flow-horizontal" style={{flexWrap: "wrap"}}>
                    <div className="flow-vertical" style={{paddingRight: "15px"}}>
                        <PalantirTextField
                            label="Schedule ID"
                            value={schedule[ConstructionActivitySchedule.columnSchema.oid]}
                            disabled
                            style={{marginBottom: "15px", width: "220px"}}
                        />
                        <PalantirTextField
                            label="Schedule Name"
                            value={schedule[ConstructionActivitySchedule.columnSchema.name]}
                            disabled
                            style={{marginBottom: "15px", width: "220px"}}
                        />
                    </div>
                    <div className="flow-vertical" style={{paddingRight: "15px"}}>
                        <PalantirTextField
                            label="Project ID"
                            value={schedule[ConstructionActivitySchedule.columnSchema.projectId]}
                            disabled
                            style={{marginBottom: "15px", width: "220px"}}
                        />
                        <PalantirDatePicker
                            label="Date"
                            value={schedule[ConstructionActivitySchedule.columnSchema.date]}
                            onChange={(date) => {
                                removeErrors(scheduleErrorPath)
                                updateSchedule({
                                    [ConstructionActivitySchedule.columnSchema.date]: date,
                                    [ConstructionActivitySchedule.columnSchema.name]: makeScheduleName(date)
                                })
                            }}
                            style={{marginBottom: "15px", width: "220px"}}
                            InputProps={{...scheduleErrorProps}}
                        />
                    </div>
                </div>

                <Text size="lg" weight={500}>Activity Completion</Text>
                <hr style={{width: "100%"}}></hr>
                <div style={{display: "grid", gridTemplateColumns: "50px 110px 110px", gridTemplateRows: `40px repeat(${entryRecords.length}, auto)`, columnGap: "20px", rowGap: "5px"}}>
                    <div style={{gridRow: 1, gridColumn: 1}}><Text size="lg" style={{borderBottom: "solid grey 1px"}}>Activity</Text></div>
                    <div style={{gridRow: 1, gridColumn: 2}}><Text size="lg" style={{borderBottom: "solid grey 1px"}}>Actual (%)</Text></div>
                    <div style={{gridRow: 1, gridColumn: 3}}><Text size="lg" style={{borderBottom: "solid grey 1px"}}>Planned (%)</Text></div>
                    {_.sortBy(entryRecords, [ConstructionActivity.columnSchema.name]).map((record, index) => {
                        const activityRecord = activityRecords.find(x => x[ConstructionActivity.columnSchema.oid]===record[ConstructionActivityScheduleEntry.columnSchema.activityId]) || {}
                        return (
                           <React.Fragment key={index}>
                                <div style={{gridRow: index + 2, gridColumn: 1, display: "flex", alignItems: "end"}}><Text size="sm" style={{}}>{activityRecord[ConstructionActivity.columnSchema.name]}</Text></div>
                                <div style={{gridRow: index + 2, gridColumn: 2}}>
                                    <PalantirTextField
                                        value={record[ConstructionActivityScheduleEntry.columnSchema.completionActual]}
                                        onChange={(x) => updateEntry(record, {[ConstructionActivityScheduleEntry.columnSchema.completionActual]: x})}
                                        InputProps={{inputComponent: TextMaskNumber}}
                                    />
                                </div>
                                <div style={{gridRow: index + 2, gridColumn: 3}}>
                                    <PalantirTextField
                                        value={record[ConstructionActivityScheduleEntry.columnSchema.completionScheduled]}
                                        onChange={(x) => updateEntry(record, {[ConstructionActivityScheduleEntry.columnSchema.completionScheduled]: x})}
                                        InputProps={{inputComponent: TextMaskNumber}}
                                    />
                                </div>
                            </React.Fragment>
                        )
                    })}
                </div>

            </div>
        </div>
    )
}